// Uses the Firebase compat SDK loaded via <script> tags in index.html.
// window.firebase is set by firebase-app-compat.js + firebase-messaging-compat.js.
import { apiFetch, peekToken, STREAM_API } from './streamApi';

const FIREBASE_CONFIG = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY            || 'AIzaSyAtJqyVPWyuR6Klb1r-0G7-djQs9ZhYLxU',
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN        || 'parvekshakapp.firebaseapp.com',
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID         || 'parvekshakapp',
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET     || 'parvekshakapp.firebasestorage.app',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '962813517310',
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

let _messaging = null;

function _getMessaging() {
  if (_messaging) return _messaging;
  if (!FIREBASE_CONFIG.appId || !VAPID_KEY) return null;
  const fb = window.firebase;
  if (!fb) return null;
  if (!fb.apps.length) fb.initializeApp(FIREBASE_CONFIG);
  _messaging = fb.messaging();
  return _messaging;
}

function _soundFile(name) {
  return `/sounds/${name}.wav`;
}

let _currentAlertAudio = null;

function playAlertSound(name) {
  const audio = new Audio(_soundFile(name));
  _currentAlertAudio = audio;
  audio.play().catch(() => {});
}

// Called when an SOS is attended/stopped elsewhere — cuts off this device's siren
// immediately if it's still playing (no-op once the clip has already finished).
function stopAlertSound() {
  if (_currentAlertAudio) {
    _currentAlertAudio.pause();
    _currentAlertAudio.currentTime = 0;
    _currentAlertAudio = null;
  }
}

async function dismissSosNotifications() {
  if (!('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const notifications = await reg.getNotifications({ tag: 'parvekshak-alert' });
    notifications.forEach((n) => n.close());
  } catch (_) {}
}

// ─── Per-camera notification grouping ─────────────────────────────────────────
// Mirrors the IndexedDB store used by firebase-messaging-sw.js (same origin, same
// DB name) so foreground-received alerts and background ones share one unread
// count/detail list per camera.

const CAM_DB_NAME = 'parvekshak-notif-groups';
const CAM_STORE = 'cameras';

function _camDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(CAM_DB_NAME, 1);
    req.onupgradeneeded = () => { req.result.createObjectStore(CAM_STORE); };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function bumpCameraGroup(cameraId, line) {
  const db = await _camDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(CAM_STORE, 'readwrite');
    const store = tx.objectStore(CAM_STORE);
    const getReq = store.get(cameraId);
    getReq.onsuccess = () => {
      const prev = getReq.result || { count: 0, lines: [] };
      const next = { count: prev.count + 1, lines: [line, ...prev.lines].slice(0, 5) };
      store.put(next, cameraId);
      tx.oncomplete = () => resolve(next);
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

async function clearAllCameraGroups() {
  const db = await _camDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(CAM_STORE, 'readwrite');
    tx.objectStore(CAM_STORE).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Call when the app becomes visible (tab focus / app open): closes any
// currently-shown notifications and resets per-camera unread counts, so the
// next alert for a camera starts a fresh group instead of resuming a stale count.
export async function clearNotificationsOnForeground() {
  if ('serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.ready;
      const notifications = await reg.getNotifications();
      notifications.forEach((n) => n.close());
    } catch (_) {}
  }
  try {
    await clearAllCameraGroups();
  } catch (_) {}
}

// This browser's FCM token, kept so logout can unregister it.
const _PUSH_TOKEN_KEY = 'prvsk_push_token';

// Logout: stop pushes to this browser — and, inside the phone app's WebView, to
// the phone. Call BEFORE resetSession(): the box needs the session's JWT to drop
// the phone by its device id. Fire-and-forget; logout never waits on it.
export function unregisterPushOnLogout() {
  let webToken = null;
  try {
    webToken = localStorage.getItem(_PUSH_TOKEN_KEY);
    localStorage.removeItem(_PUSH_TOKEN_KEY);
  } catch (_) {}
  const deviceId = window.FLUTTER_DEVICE_ID || '';
  const jwt = peekToken();
  if (webToken || (deviceId && jwt)) {
    const headers = { 'Content-Type': 'application/json' };
    if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
    // keepalive: the page navigates to /login right after this.
    fetch(`${STREAM_API}/push/unregister`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ token: webToken || '', device_id: deviceId }),
      keepalive: true,
    }).catch(() => {});
  }
  // Newer phone apps also forget their FCM token (see PushService.unregister).
  if (window.FlutterLogout) {
    try { window.FlutterLogout.postMessage('logout'); } catch (_) {}
  }
  // Drop the browser's FCM token too, so a push already queued for it can't land.
  if (webToken) {
    const messaging = _getMessaging();
    if (messaging) messaging.deleteToken().catch(() => {});
  }
}

// Registers the SW and the FCM token with the backend.
// Safe to call without a user gesture on platforms that auto-grant permission.
// On iOS PWA, this only completes if permission is already 'granted'.
export async function initPushNotifications() {
  if (!('serviceWorker' in navigator) || !('Notification' in window)) return;
  if (Notification.permission === 'denied') return;

  const messaging = _getMessaging();
  if (!messaging) {
    console.warn('[Push] Firebase config incomplete. Set REACT_APP_FIREBASE_APP_ID and REACT_APP_FIREBASE_VAPID_KEY.');
    return;
  }

  try {
    const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });

    const token = await messaging.getToken({
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });

    if (token) {
      try { localStorage.setItem(_PUSH_TOKEN_KEY, token); } catch (_) {}
      await apiFetch('/push/register', {
        method: 'POST',
        json: {
          token,
          platform: 'web',
          device_model: navigator.userAgent.slice(0, 120),
        },
      });
    }

    // Foreground messages: app is open and in focus.
    // Use registration.showNotification() (same tag as SW) so iOS doesn't
    // create a second notification when both onMessage and onBackgroundMessage fire.
    messaging.onMessage(async (payload) => {
      if (payload.data?.sos_stop === 'true') {
        stopAlertSound();
        dismissSosNotifications();
        return;
      }
      const isSOS   = payload.data?.sos   === 'true';
      const isSiren = payload.data?.siren === 'true';
      const showNotification = isSOS || payload.data?.show_notification !== 'false';
      if (!showNotification) return;

      // Doorbell camera (PRVSK-139): always its own ding-dong, even when the siren went off.
      const sound = isSOS ? 'siren_double' : payload.data?.doorbell === 'true' ? 'doorbell'
        : isSiren ? 'siren' : 'notify';
      playAlertSound(sound);

      if (Notification.permission === 'granted') {
        const title    = payload.notification?.title || payload.data?.title || 'Parvekshak Alert';
        const body     = payload.notification?.body  || payload.data?.body  || '';
        const cameraId = !isSOS ? payload.data?.camera_id : null;
        // Doorbell cameras send the alert photo (PRVSK-139) — same as the service
        // worker: shown as `image`, and as the icon, which is all macOS displays.
        const image = payload.data?.image_path || undefined;
        try {
          const reg = await navigator.serviceWorker.ready;
          if (cameraId) {
            const { count, lines } = await bumpCameraGroup(cameraId, body);
            await reg.showNotification(image ? title : (payload.data?.camera_name || title), {
              body:               image ? body : `${count} new alert${count === 1 ? '' : 's'}\n${lines.join('\n')}`,
              image,
              icon:               image || '/icon-192.png',
              badge:              '/icon-192.png',
              data:               payload.data,
              tag:                `parvekshak-camera-${cameraId}`,
              renotify:           true,
              requireInteraction: false,
            });
          } else {
            await reg.showNotification(title, {
              body,
              icon:               '/icon-192.png',
              badge:              '/icon-192.png',
              data:               payload.data,
              tag:                'parvekshak-alert',
              renotify:           true,
              requireInteraction: isSOS,
            });
          }
        } catch (_) {}
      }
    });

    // Background messages: SW sends a postMessage so the open tab plays audio
    // even when the tab is not focused (service worker handles the notification display).
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'PLAY_ALERT_SOUND') {
        playAlertSound(event.data.sound);
      } else if (event.data?.type === 'STOP_ALERT_SOUND') {
        stopAlertSound();
      }
    });

    // Mirrors the Android app's on-foreground tray clear: reset per-camera
    // unread state whenever the tab/PWA becomes visible again.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        clearNotificationsOnForeground();
      }
    });

  } catch (err) {
    console.warn('[Push] Registration failed:', err.message);
  }
}

// Call this from a user gesture (button click). Required on iOS 16.4+ PWA.
export async function requestPushPermission() {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') {
    await initPushNotifications();
    return true;
  }
  const result = await Notification.requestPermission();
  if (result === 'granted') {
    await initPushNotifications();
    return true;
  }
  return false;
}

export function pushSupported() {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
}

export function isPWA() {
  return window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
}
